import type { RefObject } from "react";
import { useCallback, useEffect, useState } from "react";

import { ErrorMessage, MultiSelectItem, Small } from "@fronterahealth/frontera-ui-components";
import { Button } from "@fronterahealth/frontera-ui-components/ui/button";
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
  useComboboxAnchor,
} from "@fronterahealth/frontera-ui-components/ui/combobox";

import { FieldLayout } from "@components/BlurBasedFormFields/InlineFormFields/FieldLayout";
import type { InlineFormFieldProps } from "@components/BlurBasedFormFields/InlineFormFields/types";

type InlineMultiSelectFieldProps<T extends Record<string, unknown>> = Omit<
  Extract<InlineFormFieldProps<T>, { inputType: "multiSelect" }>,
  "inputType"
> & {
  labelHidden?: boolean;
};

type ComboboxInputKeyDownEvent = React.KeyboardEvent<HTMLInputElement> & { preventBaseUIHandler?: () => void };

export const InlineMultiSelectField = <T extends Record<string, unknown>>({
  value,
  fieldKey,
  onSave,
  title,
  required = false,
  className,
  multiSelectItems,
  labelHidden = false,
}: InlineMultiSelectFieldProps<T>) => {
  const list =
    Array.isArray(value) && value.length > 0 && typeof value[0] === "object" && "primary" in value[0]
      ? (value as MultiSelectItem[])
      : [];
  const [selected, setSelected] = useState<MultiSelectItem[]>(list);
  const [inputValue, setInputValue] = useState("");
  const [highlightedItem, setHighlightedItem] = useState<MultiSelectItem | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const anchor = useComboboxAnchor();

  useEffect(() => {
    const next =
      Array.isArray(value) && value.length > 0 && typeof value[0] === "object" && "primary" in value[0]
        ? (value as MultiSelectItem[])
        : [];
    setSelected(next);
  }, [value]);

  const handleChange = useCallback(
    async (newSelected: MultiSelectItem[]) => {
      setSelected(newSelected);
      setError(null);
      try {
        const result = await onSave({ [fieldKey]: newSelected } as Partial<T>);
        if (!result.success && result.error) setError(result.error);
      } catch (err) {
        console.error("Error saving field:", err);
        setSelected(list);
        setError("Failed to save changes");
      }
    },
    [fieldKey, onSave, list],
  );

  const commitTypedTextAsNewChip = useCallback(
    (rawText: string) => {
      const typedText = rawText.trim();
      if (!typedText) return;
      const alreadySelected = selected.some((item) => item.primary === typedText);
      if (!alreadySelected) {
        handleChange([...selected, { id: typedText, primary: typedText }]);
      }
      setInputValue("");
    },
    [selected, handleChange],
  );

  const commitTypedChipOnEnterUnlessItemHighlighted = useCallback(
    (event: ComboboxInputKeyDownEvent) => {
      const shouldDeferToHighlightedItem = event.key !== "Enter" || highlightedItem !== undefined;
      if (shouldDeferToHighlightedItem) return;
      event.preventBaseUIHandler?.();
      commitTypedTextAsNewChip(inputValue);
    },
    [inputValue, highlightedItem, commitTypedTextAsNewChip],
  );

  const multiSelect = (
    <div className={labelHidden ? undefined : "-mt-6"}>
      {!required && (
        <div className="flex justify-end">
          <Small className="leading-6">Optional</Small>
        </div>
      )}
      <Combobox
        multiple
        autoHighlight
        items={multiSelectItems}
        itemToStringLabel={(item) => item.primary}
        isItemEqualToValue={(a, b) => (a.id && b.id ? a.id === b.id : a.primary === b.primary)}
        value={selected}
        onValueChange={handleChange}
        inputValue={inputValue}
        onInputValueChange={setInputValue}
        onItemHighlighted={setHighlightedItem}
      >
        <ComboboxChips ref={anchor as RefObject<HTMLDivElement>}>
          <ComboboxValue>
            {(values: MultiSelectItem[]) => (
              <>
                {values.map((item) => (
                  <ComboboxChip key={item.id ?? item.primary}>
                    {item.primary}
                    {item.secondary && <span className="ml-1 text-static-text-subtle">{item.secondary}</span>}
                  </ComboboxChip>
                ))}
                <ComboboxChipsInput placeholder={title} onKeyDown={commitTypedChipOnEnterUnlessItemHighlighted} />
              </>
            )}
          </ComboboxValue>
        </ComboboxChips>
        <ComboboxContent anchor={anchor}>
          <ComboboxEmpty>
            {inputValue.trim() && (
              <Button
                className="w-full justify-start font-semibold text-rio-600 py-4 px-4 text-sm cursor-pointer hover:no-underline hover:text-rio-800"
                variant="link"
                type="button"
                onClick={() => commitTypedTextAsNewChip(inputValue)}
              >
                Add &quot;{inputValue.trim()}&quot;
              </Button>
            )}
          </ComboboxEmpty>
          <ComboboxList>
            {(item: MultiSelectItem) => (
              <ComboboxItem key={item.id ?? item.primary} value={item}>
                {item.primary}
                {item.secondary && <span className="ml-2 text-static-text-subtle">{item.secondary}</span>}
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
      {error && <ErrorMessage errorMessage={error} />}
    </div>
  );

  if (labelHidden) {
    return <div className={className ?? "flex-1 min-w-0"}>{multiSelect}</div>;
  }

  return (
    <FieldLayout title={title} className={className}>
      {multiSelect}
    </FieldLayout>
  );
};
